import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { FlagIcon } from "@/components/FlagIcon";
import { RoleManagerCard } from "@/components/RoleSwitcher";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogOut, User as UserIcon, Glasses, Focus, Crown, BarChart3, Award, Flame, Trophy, Sparkles, Star, Zap, GraduationCap, Brain, Languages, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { deleteMyAccount } from "@/lib/account.functions";
import { useServerFn } from "@tanstack/react-start";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { SUPPORTED_LANGUAGES, normalizeLanguage, persistLanguage, type AppLanguage } from "@/i18n/config";

export const Route = createFileRoute("/_app/profile")({ component: ProfilePage });

type DsaIntensity = "light" | "standard" | "high";
type P = { full_name: string | null; education_level: string | null; class_year: string | null; dsa_mode_enabled: boolean; focus_mode_enabled: boolean; dsa_intensity: DsaIntensity; has_dsa: boolean; has_adhd: boolean; has_bes: boolean; level: number; total_xp: number; streak_days: number; badges: string[] | null; language: string };

const LEVEL_VALUES = ["elementari", "medie", "superiori", "universita"] as const;

const BADGE_META: Record<string, { icon: React.ReactNode; tone: string }> = {
  first_session:   { icon: <Sparkles className="h-4 w-4" />, tone: "pastel-sky" },
  streak_7:        { icon: <Flame className="h-4 w-4" />,    tone: "pastel-peach" },
  streak_30:       { icon: <Flame className="h-4 w-4" />,    tone: "pastel-peach" },
  level_5:         { icon: <Star className="h-4 w-4" />,     tone: "pastel-lilac" },
  level_10:        { icon: <Trophy className="h-4 w-4" />,   tone: "pastel-lilac" },
  quiz_perfect:    { icon: <Zap className="h-4 w-4" />,      tone: "pastel-sage" },
};

function ProfilePage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { t, i18n } = useTranslation();
  const [p, setP] = useState<P | null>(null);
  const [deleting, setDeleting] = useState(false);
  const removeAccount = useServerFn(deleteMyAccount);

  async function deleteAccount() {
    setDeleting(true);
    try {
      await removeAccount({ data: {} as never });
      await qc.cancelQueries();
      qc.clear();
      await supabase.auth.signOut();
      toast.success(t("profile.deleteDone", { defaultValue: "Account eliminato." }));
      nav({ to: "/auth", replace: true });
    } catch {
      toast.error(t("profile.deleteError", { defaultValue: "Non sono riuscito a eliminare l'account. Riprova." }));
    } finally {
      setDeleting(false);
    }
  }

  useEffect(() => { load(); }, []);
  async function load() {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    const { data } = await supabase.from("profiles").select("*").eq("id", u.user.id).maybeSingle();
    setP(data as P | null);
  }
  async function update(patch: Partial<Omit<P, "badges">>) {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user || !p) return;
    setP({ ...p, ...patch });
    await supabase.from("profiles").update(patch).eq("id", u.user.id);
    qc.invalidateQueries({ queryKey: ["profile-flags"] });
  }
  async function signOut() {
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
    toast.success(t("common.seeYou"));
    nav({ to: "/auth", replace: true });
  }

  async function changeLanguage(code: AppLanguage) {
    await update({ language: code });
    persistLanguage(code);
    await i18n.changeLanguage(code);
    toast.success(t("toast.languageUpdated"));
  }

  if (!p) return <div className="px-5 pt-8 text-sm text-muted-foreground">{t("common.loading")}</div>;
  const currentLang = normalizeLanguage(p.language ?? i18n.language);
  const LEVELS = LEVEL_VALUES.map((value) => ({ value, label: t(`profile.level${value.charAt(0).toUpperCase()}${value.slice(1)}`) }));

  return (
    <div className="space-y-5 px-5 pt-8">
      <header className="flex items-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-[var(--shadow-soft)]">
          <UserIcon className="h-7 w-7" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{p.full_name || t("profile.student")}</h1>
          <p className="text-sm text-muted-foreground capitalize">{p.education_level || "—"}</p>
        </div>
      </header>

      <div className="grid grid-cols-3 gap-3">
        <Stat label={t("profile.level")} value={p.level} />
        <Stat label={t("profile.xp")} value={p.total_xp} />
        <Stat label={t("profile.streak")} value={p.streak_days} />
      </div>

      <Link to="/stats" className="block">
        <Card className="card-soft flex items-center justify-between gap-3 border-0 p-4 transition-transform hover:scale-[1.01] active:scale-[0.99]">
          <div className="flex items-center gap-3">
            <div className="rounded-2xl bg-primary/10 p-2 text-primary"><BarChart3 className="h-5 w-5" /></div>
            <div>
              <p className="text-sm font-semibold">{t("profile.stats")}</p>
              <p className="text-xs text-muted-foreground">{t("profile.statsSub")}</p>
            </div>
          </div>
          <span className="text-sm text-muted-foreground">›</span>
        </Card>
      </Link>

      <div className="grid gap-3 sm:grid-cols-2">
        <Link to="/classes" className="block">
          <Card className="card-soft flex items-center justify-between gap-3 border-0 p-4 transition-transform hover:scale-[1.01] active:scale-[0.99]">
            <div className="flex min-w-0 items-center gap-3">
              <div className="rounded-2xl bg-accent/15 p-2 text-accent-foreground"><Users className="h-5 w-5" /></div>
              <div className="min-w-0">
                <p className="break-words text-sm font-semibold">{t("teacher.join.myClasses")}</p>
                <p className="break-words text-xs text-muted-foreground">{t("teacher.join.subtitle")}</p>
              </div>
            </div>
            <span className="text-sm text-muted-foreground">›</span>
          </Card>
        </Link>
        <Link to="/teacher" className="block">
          <Card className="card-soft flex items-center justify-between gap-3 border-0 p-4 transition-transform hover:scale-[1.01] active:scale-[0.99]">
            <div className="flex min-w-0 items-center gap-3">
              <div className="rounded-2xl bg-primary/10 p-2 text-primary"><GraduationCap className="h-5 w-5" /></div>
              <div className="min-w-0">
                <p className="break-words text-sm font-semibold">{t("teacher.dash.title")}</p>
                <p className="break-words text-xs text-muted-foreground">{t("teacher.dash.subtitle")}</p>
              </div>
            </div>
            <span className="text-sm text-muted-foreground">›</span>
          </Card>
        </Link>
        <Link to="/family" className="block">
          <Card className="card-soft flex items-center justify-between gap-3 border-0 p-4 transition-transform hover:scale-[1.01] active:scale-[0.99]">
            <div className="flex min-w-0 items-center gap-3">
              <div className="rounded-2xl bg-secondary/40 p-2 text-foreground"><Users className="h-5 w-5" /></div>
              <div className="min-w-0">
                <p className="break-words text-sm font-semibold">{t("teacher.parent.title")}</p>
                <p className="break-words text-xs text-muted-foreground">{t("teacher.role.parentDesc")}</p>
              </div>
            </div>
            <span className="text-sm text-muted-foreground">›</span>
          </Card>
        </Link>
      </div>

      <RoleManagerCard />
      <Link to="/profiles" className="block">
        <Button variant="secondary" className="min-h-11 w-full justify-start">
          {t("roleui.manage.open")}
        </Button>
      </Link>






      <Card className="card-soft border-0 p-5">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <Languages className="h-4 w-4" /> {t("profile.language")}
        </div>
        <p className="mb-3 text-xs text-muted-foreground">{t("profile.languageHint")}</p>
        <div className="grid grid-cols-2 gap-1.5 rounded-xl bg-muted p-1 sm:grid-cols-3">
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => void changeLanguage(l.code)}
              className={`flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-medium transition ${
                currentLang === l.code
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <FlagIcon code={l.code} className="h-3.5 w-5 rounded-[2px] ring-1 ring-black/10" />
                {l.nativeName}
              </span>
            </button>
          ))}
        </div>
      </Card>

      <Card className="card-soft border-0 p-5">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <Award className="h-4 w-4" /> {t("profile.achievements")}
        </div>
        {p.badges && p.badges.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {p.badges.map((b) => {
              const meta = BADGE_META[b] ?? { icon: <Award className="h-4 w-4" />, tone: "pastel-sky" };
              const label = t(`badges.${b}.label`, { defaultValue: b });
              return (
                <span key={b} className={`${meta.tone} inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-foreground`}>
                  {meta.icon} {label}
                </span>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t("profile.noBadges")}</p>
        )}
      </Card>

      <Card className="card-soft border-0 p-5">
        <p className="mb-3 text-sm font-semibold text-muted-foreground">{t("profile.accessibility")}</p>
        <ToggleRow icon={<Glasses className="h-4 w-4" />} label={t("profile.dsa")} desc={t("profile.dsaHint")}
          checked={p.dsa_mode_enabled} onChange={(v) => update({ dsa_mode_enabled: v })} />
        {p.dsa_mode_enabled && (
          <div className="mt-3 border-t border-border pt-3">
            <Label className="text-sm font-medium">{t("profile.dsaIntensity")}</Label>
            <p className="mb-2 text-xs text-muted-foreground">{t("profile.dsaIntensityHint")}</p>
            <div className="grid grid-cols-3 gap-1.5 rounded-xl bg-muted p-1">
              {(["light", "standard", "high"] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => update({ dsa_intensity: v })}
                  className={`truncate rounded-lg px-1 py-1.5 text-xs font-medium transition ${
                    (p.dsa_intensity ?? "standard") === v
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {v === "light" ? t("profile.intensityLight") : v === "standard" ? t("profile.intensityStandard") : t("profile.intensityHigh")}
                </button>
              ))}
            </div>
            <p className="mt-2 rounded-lg bg-muted/40 p-2 text-xs text-foreground/80">
              {t("profile.dsaPreview")} <span className="dsa-key"><span className="dsa-head">Foto</span>sintesi</span> è il <span className="dsa-key"><span className="dsa-head">proc</span>esso</span> con cui le <span className="dsa-key"><span className="dsa-head">pian</span>te</span> producono energia.
            </p>
          </div>
        )}
        <ToggleRow icon={<Focus className="h-4 w-4" />} label={t("profile.focusMode")} desc={t("profile.focusModeHint")}
          checked={p.focus_mode_enabled} onChange={(v) => update({ focus_mode_enabled: v })} />
      </Card>

      <Card className="card-soft border-0 p-5">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <GraduationCap className="h-4 w-4" /> {t("profile.learningProfile")}
        </div>
        <p className="mb-3 text-xs text-muted-foreground">{t("profile.learningProfileHint")}</p>

        <div className="space-y-3">
          <div>
            <Label className="text-sm font-medium">{t("profile.educationLevel")}</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-1.5 rounded-xl bg-muted p-1 sm:grid-cols-4">
              {LEVELS.map((l) => (
                <button
                  key={l.value}
                  type="button"
                  onClick={() => update({ education_level: l.value })}
                  className={`truncate rounded-lg px-1 py-1.5 text-xs font-medium transition ${
                    p.education_level === l.value ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="class_year" className="text-sm font-medium">{t("profile.classYear")}</Label>
            <Input
              id="class_year"
              className="mt-1.5"
              placeholder={t("profile.classYearPlaceholder")}
              defaultValue={p.class_year ?? ""}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if ((p.class_year ?? "") !== v) update({ class_year: v || null });
              }}
            />
          </div>
        </div>

        <div className="mt-4 border-t border-border pt-2">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
            <Brain className="h-4 w-4" /> {t("profile.learningNeedsHint")}
          </div>
          <ToggleRow icon={<Glasses className="h-4 w-4" />} label={t("profile.dsaLabel")} desc={t("profile.dsaFullHint")}
            checked={p.has_dsa} onChange={(v) => update({ has_dsa: v })} />
          <ToggleRow icon={<Focus className="h-4 w-4" />} label="ADHD" desc={t("profile.adhdHint")}
            checked={p.has_adhd} onChange={(v) => update({ has_adhd: v })} />
          <ToggleRow icon={<Sparkles className="h-4 w-4" />} label={t("profile.besLabel")} desc={t("profile.besHint")}
            checked={p.has_bes} onChange={(v) => update({ has_bes: v })} />
        </div>
      </Card>


      <Button variant="ghost" className="w-full text-destructive" onClick={signOut}>
        <LogOut className="mr-2 h-4 w-4" /> {t("common.signOut")}
      </Button>

      <div className="flex justify-center gap-4 text-xs text-muted-foreground">
        <Link to="/privacy" className="hover:text-foreground">{t("common.privacy")}</Link>
        <Link to="/terms" className="hover:text-foreground">{t("common.terms")}</Link>
      </div>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" size="sm" className="w-full text-xs text-muted-foreground">
            {t("profile.deleteAccount", { defaultValue: "Elimina il mio account" })}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("profile.deleteAccount", { defaultValue: "Elimina il mio account" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("profile.deleteAccountDesc", {
                defaultValue:
                  "Cancelli per sempre account, appunti, progressi e streak. L'operazione non si può annullare.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", { defaultValue: "Annulla" })}</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(e) => { e.preventDefault(); void deleteAccount(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("profile.deleteConfirm", { defaultValue: "Elimina definitivamente" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="card-soft rounded-2xl p-4 text-center">
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
function ToggleRow({ icon, label, desc, checked, onChange }: { icon: React.ReactNode; label: string; desc: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0 [&+&]:border-t [&+&]:border-border">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-muted p-1.5 text-foreground/70">{icon}</div>
        <div>
          <Label className="text-sm font-medium">{label}</Label>
          <p className="text-xs text-muted-foreground">{desc}</p>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}